/**
 * Endpoints de **Finanzas → Kardex Valorizado** (`/finanzas/kardex-valorizado`).
 *
 * Cuatro llamadas: el catálogo de combustibles, el formato 13.1 del mes, la
 * exportación a Excel y el cierre de mes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { ProductoValorizado, ReporteValorizado } from './kardex-valorizado.types';

export const valorizadoKeys = {
  todo: ['finanzas', 'kardex-valorizado'] as const,
  productos: ['finanzas', 'kardex-valorizado', 'productos'] as const,
  mes: (producto: number, desde: string, hasta: string) =>
    ['finanzas', 'kardex-valorizado', 'mes', producto, desde, hasta] as const,
};

/** Los parámetros del formato: producto y rango de fechas. */
export interface FiltrosValorizado {
  product: number;
  startDate: string;
  endDate: string;
}

const cadenaDeValorizado = (filtros: FiltrosValorizado) =>
  new URLSearchParams({
    product: String(filtros.product),
    startDate: filtros.startDate,
    endDate: filtros.endDate,
  }).toString();

export const valorizadoApi = {
  productos: (signal?: AbortSignal) =>
    apiClient.get<{ productos: ProductoValorizado[] }>(
      '/api/finanzas/kardex-valorizado/productos',
      { signal },
    ),

  mes: (filtros: FiltrosValorizado, signal?: AbortSignal) =>
    apiClient.get<ReporteValorizado>(
      `/api/finanzas/kardex-valorizado?${cadenaDeValorizado(filtros)}`,
      { signal },
    ),

  urlExcel: (filtros: FiltrosValorizado) =>
    `/api/finanzas/kardex-valorizado/excel?${cadenaDeValorizado(filtros)}`,

  cerrarMes: (cuerpo: {
    product_id: number;
    month: number;
    year: number;
    quantity: number;
    price: number;
    total: number;
    reason: string;
  }) => apiClient.post<{ id: number; message: string }>('/api/finanzas/kardex-valorizado/cierre', {
    body: cuerpo,
  }),
};

/** Catálogo de combustibles. */
export function useProductosValorizado() {
  return useQuery({
    queryKey: valorizadoKeys.productos,
    queryFn: ({ signal }) => valorizadoApi.productos(signal),
    staleTime: 5 * 60_000,
  });
}

/** El formato 13.1 del mes (sólo cuando se pulsa *Consultar*). */
export function useFormatoValorizado(filtros: FiltrosValorizado | null) {
  return useQuery({
    queryKey: [...valorizadoKeys.todo, 'mes', filtros ?? 'sin-consultar'] as const,
    queryFn: ({ signal }) => valorizadoApi.mes(filtros as FiltrosValorizado, signal),
    enabled: filtros !== null,
  });
}

/** Cierre del mes con el saldo final del período. */
export function useCerrarMesValorizado() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (cuerpo: Parameters<typeof valorizadoApi.cerrarMes>[0]) =>
      valorizadoApi.cerrarMes(cuerpo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: valorizadoKeys.todo }),
  });
}
